import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo, services, industries, partners } from '../../mockData';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ✅ COMMON CLICK HANDLER
  const handleLinkClick = () => {
    setActiveDropdown(null);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigation = [
    {
      name: 'Services',
      href: '/services',
      dropdown: services.map(s => ({ name: s.name, href: `/services/${s.slug}` }))
    },
    {
      name: 'Industries',
      href: '/industries',
      dropdown: industries.map(i => ({ name: i.name, href: `/industries/${i.slug}` }))
    },
    { name: 'Client Success', href: '/client-success' },
    { name: 'Insights', href: '/insights' },
    {
      name: 'Partners',
      href: '/partner-ecosystem',
      dropdown: [{ name: 'View All Partners', href: '/partner-ecosystem' }]
    },
    {
      name: 'About',
      href: '/about',
      dropdown: [
        { name: 'Company Overview', href: '/about' },
        { name: 'Leadership Team', href: '/about/leadership' },
        { name: 'Global Locations', href: '/about/locations' },
        { name: 'Awards & Recognition', href: '/about/awards' },
        // { name: 'ESG & Sustainability', href: '/about/esg' },
        // { name: 'Diversity & Inclusion', href: '/about/diversity' },
        // { name: 'Innovation Lab', href: '/about/innovation-lab' }
      ]
    },
    { name: 'Careers', href: '/careers' }
  ];

  const handleDropdownEnter = (index) => {
    setActiveDropdown(index);
  };

  const handleDropdownLeave = () => {
    setActiveDropdown(null);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div>
            <Link to="/" onClick={handleLinkClick}>
              <img
                src="/images/logo_black.png"
                alt="Techspr Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigation.map((item, index) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.dropdown && handleDropdownEnter(index)}
                onMouseLeave={handleDropdownLeave}
              >
                <Link
                  to={item.href}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-1 ${location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                      ? 'text-indigo-600 bg-indigo-50'
                      : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50'
                    }`}
                >
                  <span>{item.name}</span>
                  {item.dropdown && <ChevronDown className="w-4 h-4" />}
                </Link>


                {item.name === 'Services' && activeDropdown === index && (
                  <div className="absolute top-full left-1/2 -translate-x-[7%]  w-[900px] bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 z-50">

                    <div className="grid grid-cols-12 gap-8">

                      {/* LEFT */}
                      <div className="col-span-3">
                        <Link
                          to="/services"
                          className="flex items-center justify-between text-lg font-semibold text-gray-900 hover:text-indigo-600"
                        >
                          All Services →
                        </Link>
                        <p className="mt-3 text-sm text-gray-500">
                          Explore our complete digital engineering services portfolio.
                        </p>
                      </div>

                      {/* CENTER – SERVICES */}
                      <div className="col-span-6 grid grid-cols-2 gap-x-8 gap-y-4">
                        {services.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            onClick={handleLinkClick}
                            className="text-sm text-gray-700 hover:text-indigo-600 transition"
                          >
                            {s.name}
                          </Link>
                        ))}
                      </div>

                      {/* RIGHT */}
                      <div className="col-span-3">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">
                          Accelerators & IP
                        </h4>
                        <p className="text-sm text-gray-500">
                          Scale faster with enterprise accelerators and intellectual property
                          built for long-term impact.
                        </p>
                      </div>

                    </div>
                  </div>
                )}

                




                {/* Dropdown Menu */}
                {item.name != 'Services'  && activeDropdown === index && (
                  <div className="absolute top-full left-0 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="max-h-[70vh] overflow-y-auto">
                      {item.dropdown.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          to={subItem.href}
                          onClick={handleLinkClick}
                          className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors duration-150"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              asChild
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="max-h-[calc(100vh-5rem)] overflow-y-auto px-4 py-4 space-y-2">
            {navigation.map((item) => (
              <div key={item.name}>
                <Link
                  to={item.href}
                  className="block px-4 py-3 text-base font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg transition-colors duration-150"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
                {item.dropdown && (
                  <div className="ml-4 mt-2 space-y-1">
                    {item.dropdown.slice(0, 5).map((subItem, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subItem.href}
                        onClick={handleLinkClick}
                        className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-indigo-600 rounded-lg transition-colors duration-150"
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Button
              asChild
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white mt-4"
            >
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                Contact Us
              </Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;